import React, { useState, useEffect, useRef } from "react";
import AttendanceTable from "../../Components/Common/AdminAttendanceTable";
import { Button, Spin, message } from "antd";
import { getRequest } from "../../services/index";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify"; 

const AdminAttendanceModal = ({ currentClass, section, onClose }) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const [currentDate, setCurrentDate] = useState("");
  const [studentsData, setStudentsData] = useState({ students: [] });
  const [attendanceToday, setAttendanceToday] = useState(null);
  const [isAttendanceMarkedToday, setIsAttendanceMarkedToday] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const attendanceTableRef = useRef();

  useEffect(() => {
    const formatDate = () => {
      const date = new Date();
      return date.toDateString();
    };
    setCurrentDate(formatDate());
  }, []);

  useEffect(() => {
    if (currentClass && section) {
      fetchStudentsData();
    }
  }, [currentClass, section]);

  useEffect(() => {
    checkAttendanceStatus();
  }, [attendanceToday]);

  const fetchStudentsData = async () => {
    setLoading(true);
    try {
      let sectionId = null;
      
      if (currentClass?.subjects && currentClass.subjects.length > 0) {
        const subjectWithSection = currentClass.subjects.find(subject => 
          subject.days && subject.days.some(day => day.section === section)
        );
        
        if (subjectWithSection) {
          sectionId = subjectWithSection.sectionId;
        }
      }
      
      const classSectionId = sectionId || currentClass.id;
      
      console.log("Using section ID:", classSectionId);
      const response = await getRequest(
        `/api/ClassAssignment/GetStudentsInClassBySection?classSectionId=${classSectionId}`
      );

      if (response && Array.isArray(response)) {
        setStudentsData({
          students: response.map(student => ({
            ...student,
            attendancePercentage: student.attendancePercentage || 0
          })),
          lastAttendance: null
        });
      } else if (response?.data) {
        const processedData = {
          students: Array.isArray(response.data) 
            ? response.data.map(student => ({
                ...student,
                attendancePercentage: student.attendancePercentage || 0
              })) 
            : response.data.students?.map(student => ({
                ...student,
                attendancePercentage: student.attendancePercentage || 0
              })) || [],
          lastAttendance: response.data.lastAttendance || null
        };
        setStudentsData(processedData);
        setAttendanceToday(processedData.lastAttendance);
      } else {
        console.error("Invalid response format:", response);
        setStudentsData({ students: [] });
      }
    } catch (error) {
      console.error("Error fetching students data:", error.message);
      setStudentsData({ students: [] });
      toast.error("Failed to fetch students data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const checkAttendanceStatus = () => {
    if (attendanceToday) {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      const attendanceDate = new Date(attendanceToday);
      const attendanceDateStr = attendanceDate.toISOString().split('T')[0];
      
      // Check if attendance has been marked today
      const isMarkedToday = todayStr === attendanceDateStr;
      setIsAttendanceMarkedToday(isMarkedToday);
      
      if (isMarkedToday) {
        message.info(`Attendance for this section has already been marked today.`);
      }
    } else {
      setIsAttendanceMarkedToday(false);
    }
  };

  const handleSaveAttendance = async () => {
    if (attendanceTableRef.current) {
      setSaveLoading(true);
      try {
        const success = await attendanceTableRef.current.triggerSaveAttendance();
        if (success) {
          // If saving was successful, update the attendance status immediately
          setIsAttendanceMarkedToday(true);
          
          // Update the attendance date to today
          const today = new Date();
          setAttendanceToday(today.toISOString());
          
          // Refresh the students data to get the updated attendance status
          await fetchStudentsData();
        }
      } catch (error) {
        console.error("Error saving attendance:", error);
      } finally {
        setSaveLoading(false);
      }
    }
  };

  // Get the subject information for the current section
  const subjectForSection = currentClass?.subjects?.find(subject => 
    subject.days && subject.days.some(day => day.section === section)
  );
  
  const classData = {
    className: currentClass?.className,
    sectionName: section,
    subjectId: subjectForSection?.subjectId || currentClass?.subjects?.[0]?.subjectId,
    subjectName: subjectForSection?.name || currentClass?.subjects?.[0]?.name,
    id: currentClass?.id, // Class ID
    sectionId: subjectForSection?.sectionId || "", 
  };

  return (
    <div className="container-fluid px-2 py-2 shadow-md bg-white rounded">
      <Spin spinning={loading}>
        <div className="row mt-4">
          <div className="col-md-9">
            <h4 className="fw-bold heading24px">
              {`${currentClass?.className} ${section} - ${subjectForSection?.name || currentClass?.subjects?.[0]?.name || "No Subject"}`}
            </h4>
          </div>
          <div className="col-md-3">
            <div className="w-100 d-flex align-items-center h-100 gap-3 justify-content-end">
              <h6 className="p-0 m-0">Date</h6>
              <h6 className="mr-5 fw-bold">{currentDate}</h6>
            </div>
          </div>
        </div>
        
        {isAttendanceMarkedToday && (
          <div className="row mt-3">
            <div className="col-md-12">
              <div className="alert alert-info" role="alert">
                Attendance for this section has already been marked today. You cannot mark it again.
              </div>
            </div>
          </div>
        )}
        
        <div className="row mt-3">
          <div className="col-md-12">
            {!loading && (
              <AttendanceTable
                classData={classData}
                myAttendaceData={studentsData}
                ref={attendanceTableRef}
                fetchAttendanceData={fetchStudentsData}
                readOnly={isAttendanceMarkedToday}
              />
            )}
          </div>
        </div>
        <div className="row mt-3">
          <div
            className="col-md-12 d-flex flex-column flex-md-row justify-content-center justify-content-md-end gap-3 mt-3"
            dir={isArabic ? "rtl" : "ltr"}
          >
            <Button
              onClick={onClose}
              className="btnFooter w-100 w-md-auto"
              style={{ backgroundColor: "#EDEBF1", color: "#463C77" }}
            >
              {t("Cancel")}
            </Button>
            <Button
              className={`btnFooter w-100 w-md-auto`}
              style={{
                backgroundColor: !isAttendanceMarkedToday ? "#463C77" : "#D3D3D3",
                color: "white",
                pointerEvents: !isAttendanceMarkedToday ? "auto" : "none",
                cursor: !isAttendanceMarkedToday ? "pointer" : "not-allowed",
              }}
              onClick={!isAttendanceMarkedToday ? handleSaveAttendance : undefined}
              disabled={isAttendanceMarkedToday || saveLoading}
              loading={saveLoading}
            >
              {isAttendanceMarkedToday ? t("Already Marked") : t("Save Attendance")}
            </Button>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default AdminAttendanceModal;